/**
 * TypeScript types for SRS (Spaced Repetition System).
 *
 * A card is a note with `note_kind='srs_card'`. The backend lifts every
 * FSRS field out of `note.data` into a flat response shape (see backend
 * `src.srs.schemas.SRSCard.from_note`), so the frontend never has to dig
 * into `data.*` directly to render a card's scheduling state.
 */

// ===================
// Enums
// ===================

export type SRSCardState = "new" | "learning" | "review" | "relearning";

/** 1=Again, 2=Hard, 3=Good, 4=Easy — closed set, enforced server-side. */
export type SRSRating = 1 | 2 | 3 | 4;

export type SRSLens =
  | "due"
  | "upcoming"
  | "reviewed"
  | "suspended"
  | "archived"
  | "all";

/** LLM-graded recall verdict from POST /api/srs/cards/{id}/grade. */
export type GradeVerdict = "correct" | "partial" | "wrong";

export type SRSReviewType =
  | "recall"
  | "explain"
  | "multiple_choice"
  | "free_response"
  | "true_false"
  | "cloze";

// ===================
// Card model — mirrors backend SRSCard
// ===================

export interface SRSCard {
  id: string;
  title: string | null;
  raw_text: string | null;
  description: string | null;
  note_kind: "srs_card";
  note_type_id: string | null;
  note_type_title: string | null;
  has_embedding: boolean;
  created_at: string;
  updated_at: string;

  // FSRS state (lifted from note.data on the server)
  state: SRSCardState;
  step: number | null;
  stability: number | null;
  difficulty: number | null;
  due: string;
  last_review: string | null;
  lapse_count: number;
  suspended: boolean;
  archived: boolean;
  buried_until: string | null;
  review_types: string[];
  scheduler_type: string;
  model_version: string | null;

  // Card content (also from data; falls back to title/raw_text)
  prompt: string | null;
  answer: string | null;
  image_url: string | null;

  /** Passthrough of the underlying data field for non-canonical extras. */
  data: Record<string, unknown> | null;
}

// ===================
// Requests
// ===================

export interface SRSCardCreateInput {
  title?: string | null;
  content: string;
  prompt?: string | null;
  answer?: string | null;
  parent_note_id?: string | null;
  category_ids?: string[] | null;
  scheduler_type?: string;
  review_types?: string[];
  image_url?: string | null;
}

export interface CardsFromSourceInput {
  source_note_id: string;
  count?: number;
  category_ids?: string[] | null;
}

export interface ReviewRequestInput {
  card_id: string;
  rating: SRSRating;
  review_type?: string;
  review_duration_ms?: number;
  answer_given?: string;
  session_id?: string;
}

// ===================
// Responses
// ===================

export interface ReviewResponse {
  card_id: string;
  rating: number;
  new_state: SRSCardState;
  new_stability: number;
  new_difficulty: number;
  next_due: string;
  interval_days: number;
  review_log_id: string;
}

export interface ReviewLogItem {
  id: string;
  card_id: string;
  rating: number;
  review_datetime: string;
  review_duration_ms: number | null;
  review_type: string;
  state_before: string;
  stability_before: number | null;
  difficulty_before: number | null;
  scheduled_days: number | null;
  elapsed_days: number | null;
  was_correct: boolean | null;
  model_used: string | null;
  model_version: string | null;
}

export interface SchedulePreview {
  again_interval: string;
  hard_interval: string;
  good_interval: string;
  easy_interval: string;
  again_due: string;
  hard_due: string;
  good_due: string;
  easy_due: string;
}

export interface DueCardsResponse {
  cards: SRSCard[];
  total_due: number;
  new_count: number;
  review_count: number;
  learning_count: number;
}

export interface UpcomingCardsResponse {
  cards: SRSCard[];
}

export interface CardLibraryResponse {
  cards: SRSCard[];
  total: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

export interface NextDueCountdown {
  /** `null` when the library is empty. `0` when something is due now. */
  seconds_until_next: number | null;
  due_now: number;
  next_card: { id: string; title: string | null; due: string } | null;
}

export interface SRSStats {
  total_cards: number;
  cards_due_today: number;
  cards_due_now: number;
  new_cards: number;
  learning_cards: number;
  review_cards: number;
  relearning_cards: number;
  total_reviews: number;
  reviews_today: number;
  average_retention: number | null;
  current_streak_days: number;
  longest_streak_days: number;
}

// ===================
// Display helpers
// ===================

export const RATING_LABEL: Record<SRSRating, string> = {
  1: "Again",
  2: "Hard",
  3: "Good",
  4: "Easy",
};

export const RATING_SHORTCUT: Record<SRSRating, string> = {
  1: "1",
  2: "2",
  3: "3",
  4: "4",
};

export const STATE_LABEL: Record<SRSCardState, string> = {
  new: "New",
  learning: "Learning",
  review: "Review",
  relearning: "Relearning",
};

/** Tailwind class fragments per state — used by the badge / card border. */
export const STATE_COLOR: Record<SRSCardState, string> = {
  new: "text-info bg-info-soft border-info/30",
  learning: "text-warn bg-warn-soft border-warn/30",
  review: "text-ok bg-ok-soft border-ok/30",
  relearning: "text-danger bg-danger-soft border-danger/30",
};

/**
 * Resolve the prompt side of a card. If `prompt` is set it wins; otherwise
 * the title is the prompt. Final fallback: "Untitled card".
 */
export function cardPrompt(card: Pick<SRSCard, "prompt" | "title">): string {
  return card.prompt?.trim() || card.title?.trim() || "Untitled card";
}

/**
 * Resolve the answer side of a card. If `answer` is set it wins; otherwise
 * the raw_text is the answer.
 */
export function cardAnswer(
  card: Pick<SRSCard, "answer" | "raw_text">,
): string {
  return card.answer?.trim() || card.raw_text?.trim() || "";
}

// ===================
// Study notes (Learn tab)
//
// Mirrors backend src/srs/schemas.py one-to-one. `STUDY_NOTE_KIND_*`
// constants power filter pills, chips, and ordering.
// ===================

export type StudyNoteKind =
  | "concept"
  | "example"
  | "counterexample"
  | "memory_hook"
  | "reference"
  | "question";

export const STUDY_NOTE_KIND_ORDER: StudyNoteKind[] = [
  "concept",
  "example",
  "counterexample",
  "memory_hook",
  "reference",
  "question",
];

export const STUDY_NOTE_KIND_LABEL: Record<StudyNoteKind, string> = {
  concept: "Concept",
  example: "Example",
  counterexample: "Counterexample",
  memory_hook: "Memory hook",
  reference: "Reference",
  question: "Question",
};

/** Tailwind tone fragments per kind — small chip on the StudyNoteCard. */
export const STUDY_NOTE_KIND_TONE: Record<StudyNoteKind, string> = {
  concept: "text-primary",
  example: "text-primary",
  counterexample: "text-primary",
  memory_hook: "text-primary",
  reference: "text-primary",
  question: "text-primary",
};

export interface StudyNote {
  id: string;
  title: string | null;
  raw_text: string | null;
  note_kind: string;
  data: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
  kind: StudyNoteKind;
  attached_at: string | null;
  target_id: string;
}

export interface StudyNoteCreateInput {
  content: string;
  title?: string | null;
  kind?: StudyNoteKind;
}

export interface StudyNoteListResponse {
  notes: StudyNote[];
  total: number;
}

// ===================
// Card edit + LLM grading (Concept-cockpit)
// ===================

export interface SRSCardUpdateInput {
  title?: string | null;
  raw_text?: string | null;
  description?: string | null;
  prompt?: string | null;
  answer?: string | null;
  image_url?: string | null;
  review_types?: string[] | null;
  scheduler_type?: string | null;
}

export interface GradeRequest {
  answer: string;
  strict?: boolean;
}

export interface GradeResponse {
  verdict: GradeVerdict;
  /** 1=Again 2=Hard 3=Good 4=Easy — advisory; the learner picks final. */
  advisory_rating: SRSRating;
  explanation: string;
  missed_points: string[];
}
